import React from "react"
import Box from "@mui/material/Box"
import List from "@mui/material/List"
import { IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import NavbarEntry, { NavbarDivider } from "./NavbarEntry"
import UserService, { UserRole } from "src/UserService"
import { useCompany, useCommunities } from "src/providers"
import { HomeEntry } from "./HomeEntry"
import { MyAssignmentsEntry } from "./MyAssignmentsEntry"
import { ToggleMenu } from "./ToggleMenu"
import { NavbarDrawer } from "./NavbarDrawer"
import { useResponsive } from "src/utils/useResponsive"
import { TabletEntries } from "./TabletEntries"
import { scrollbarMixin } from "src/providers/AppProviders"
import { usePendingDocuments } from "src/utils/usePendingDocuments"

export const Navbar = ({
  open,
  setOpen
}: {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { isMobile } = useResponsive()
  const hasPendingDocuments = usePendingDocuments()
  const { isOrgAdmin } = useCompany()
  const { isGatedCommunityAdminInAny } = useCommunities()
  const showGatedCommunitiesEntry = isGatedCommunityAdminInAny && !isOrgAdmin && !isMobile

  // Auto-close drawer on mobile when navigating
  const handleNavigation = () => {
    if (isMobile) {
      setOpen(false)
    }
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <NavbarDrawer
          variant={isMobile ? "temporary" : "permanent"}
          anchor={isMobile ? "right" : "left"}
          open={open}
          onClose={isMobile ? () => setOpen(false) : undefined}
          ModalProps={{
            keepMounted: true // Better mobile performance
          }}
        >
          {/* Mobile Close Button */}
          {isMobile && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 1
              }}
            >
              <IconButton
                onClick={() => setOpen(false)}
                sx={{
                  color: "#01294E",
                  "&:hover": {
                    backgroundColor: "rgba(1, 41, 78, 0.04)"
                  }
                }}
                aria-label="close menu"
              >
                <CloseIcon fontSize="large" />
              </IconButton>
            </Box>
          )}

          <Box
            sx={
              !isMobile
                ? {
                  overflowX: "hidden",
                  overflowY: "auto",
                  ...scrollbarMixin()
                }
                : { flex: 1 }
            }
          >
            <HomeEntry open={open} onNavigate={handleNavigation} />
            <TabletEntries
              open={open}
              type={"KM"}
              additionalEntries={[
                {
                  iconPath: hasPendingDocuments
                    ? "/exfluency/icon-KB-notification.svg"
                    : "/exfluency/portal-icon_data-sources.svg",
                  label: "Knowledge Base",
                  path: "/data-sources",
                  name: "data-sources",
                  show:
                    UserService.hasRoles([UserRole.DATA_SOURCE_ADMIN]) &&
                    UserService.hasRoles([UserRole.KNOWLEDGE_MINER])
                }
              ]}
              onNavigate={handleNavigation}
            />
            <TabletEntries open={open} type={"OUTSOURCING"} onNavigate={handleNavigation} />
            <TabletEntries open={open} type={"INSOURCING"} onNavigate={handleNavigation} />
            <NavbarDivider open={open} />
            <List>
              {UserService.hasRoles([UserRole.BUYER]) && (
                <NavbarEntry
                  open={open}
                  iconPath="/exfluency/portal-icon_my-files.svg"
                  label="My files"
                  path="/files"
                  key="my-files"
                  data-testid={`menu-my-files-button`}
                  onNavigate={handleNavigation}
                />
              )}
              {UserService.hasRoles([UserRole.BUYER, UserRole.REDACTOR, UserRole.ENHANCER, UserRole.TRUST_MINER]) &&
                !isMobile && <MyAssignmentsEntry open={open} onNavigate={handleNavigation} />}
              <NavbarEntry
                open={open}
                key="help"
                iconPath="/exfluency/icon_HelpCenter.svg"
                label="Help Center"
                path="/help"
                data-testid={`menu-help-button`}
                onNavigate={handleNavigation}
              />
              {UserService.hasRoles([
                UserRole.EXFLUENCY_ADMIN,
                UserRole.PLATFORM_MANAGER,
                UserRole.GATED_COMMUNITY_MANAGER,
                UserRole.GC_PROJECT_MANAGER,
                UserRole.SUPPORT,
                UserRole.LANGUAGE_MINER
              ]) &&
                process.env.REACT_APP_DASHBOARDS_URL &&
                !isMobile && (
                  <NavbarEntry
                    open={open}
                    key="dashboards"
                    iconPath="/exfluency/portal-icon_admin-dashboard.svg"
                    label="Admin Portal"
                    path={process.env.REACT_APP_DASHBOARDS_URL}
                    external
                    data-testid={`menu-dashboards-button`}
                    onNavigate={handleNavigation}
                  />
                )}
            </List>
          </Box>
          <Box sx={{ overflow: "visible" }}>
            <List>
              {isOrgAdmin && !isMobile && (
                <NavbarEntry
                  open={open}
                  key="company"
                  iconPath="/exfluency/icons/icon-company.svg"
                  label="My Company"
                  path="/company"
                  data-testid="menu-company-button"
                  onNavigate={handleNavigation}
                />
              )}
              {showGatedCommunitiesEntry && (
                <NavbarEntry
                  open={open}
                  key="gated-communities"
                  iconPath="/exfluency/icons/icon-community.svg"
                  label="Knowledge Bases"
                  path="/knowledge-bases"
                  data-testid="menu-knowledge-bases-button"
                  onNavigate={handleNavigation}
                />
              )}
              <NavbarEntry
                open={open}
                key="profile"
                iconPath="/exfluency/portal-icon_profil.svg"
                label="My Profile"
                path="/profile"
                data-testid={`menu-profile-button`}
                onNavigate={handleNavigation}
              />
            </List>
            <NavbarDivider open={open} />
            {!isMobile && <ToggleMenu open={open} setOpen={setOpen} />}
            <List>
              <NavbarEntry
                open={open}
                key="logout"
                iconPath="/exfluency/portal-icon_sign-out.svg"
                label="Sign out"
                path="/logout"
                data-testid={`menu-logout-button`}
                onNavigate={handleNavigation}
              />
            </List>
          </Box>
        </NavbarDrawer>
      </Box>
    </>
  )
}
